import { __spreadArray } from 'tslib';
import * as React from 'react';
import { classNames, liveAnnounce } from 'uitk-react-utils';
import { useLocalization } from '@shared-ui/localization-context';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkTableCellTag, UitkTableFrozenColumnDirection, UitkTableSortDirection, UitkTableSortIconName, UitkTableCellPadding, UitkTableCellHeaderScope } from './types.esm.js';
import styles from './uitk-tables.css';
import UitkTableCellContext from './uitk-table-context.esm.js';
import { UitkTableContext } from './uitk-table.esm.js';

var getClassNameList = classNames.bind(styles);
var UitkTableCell = function (props) {
    var _a;
    var _b;
    var cellContext = React.useContext(UitkTableCellContext);
    var formatText = useLocalization().formatText;
    var align = props.align, border = props.border, children = props.children, className = props.className, columnWidth = props.columnWidth, colSpan = props.colSpan, description = props.description, isCellExpandable = props.isCellExpandable, id = props.id, frozenColumn = props.frozenColumn, frozenRegionsStyle = props.frozenRegionsStyle, fixedWidthColumn = props.fixedWidthColumn, highlight = props.highlight, onClick = props.onClick, onSort = props.onSort, padding = props.padding, isSortable = props.isSortable, scope = props.scope, striped = props.striped, _c = props.tag, Tag = _c === void 0 ? (_b = cellContext.tag) !== null && _b !== void 0 ? _b : UitkTableCellTag.data : _c;
    var _d = React.useState(), sortDirection = _d[0], setSortDirection = _d[1];
    var tableContext = React.useContext(UitkTableContext);
    var baseClass = 'uitk-table-cell';
    var isHeaderCell = Tag === UitkTableCellTag.header || cellContext.tag === UitkTableCellTag.header;
    var borderClasses = border &&
        (Array.isArray(border)
            ? __spreadArray([], border.map(function (borderDirection) { return "".concat(baseClass, "-border-").concat(borderDirection); }), true) : "".concat(baseClass, "-border-").concat(border));
    var cellClassNames = getClassNameList(baseClass, borderClasses, "".concat(baseClass, "-padding-").concat(padding), (align === null || align === void 0 ? void 0 : align.horizontal) && "".concat(baseClass, "-align-").concat(align.horizontal), (align === null || align === void 0 ? void 0 : align.vertical) && "".concat(baseClass, "-align-").concat(align.vertical), (_a = {},
        _a["".concat(baseClass, "-striped")] = striped,
        _a["".concat(baseClass, "-header")] = isHeaderCell,
        _a["".concat(baseClass, "-header-sorting")] = isSortable,
        _a["".concat(baseClass, "-header-sorting-").concat(sortDirection)] = isSortable && sortDirection,
        _a["".concat(baseClass, "-frozen-").concat(UitkTableFrozenColumnDirection.get(frozenColumn))] = frozenColumn,
        _a["".concat(baseClass, "-frozen-").concat(UitkTableFrozenColumnDirection.get(frozenColumn), "-gradient")] = frozenColumn &&
            frozenRegionsStyle !== 'columnBorder-rowGradient' &&
            frozenRegionsStyle !== 'columnNoBorder-rowGradient' &&
            frozenRegionsStyle !== 'onlyBorders',
        _a["".concat(baseClass, "-frozen-").concat(UitkTableFrozenColumnDirection.get(frozenColumn), "-border")] = frozenColumn && (frozenRegionsStyle === 'columnBorder-rowGradient' || frozenRegionsStyle === 'onlyBorders'),
        _a["".concat(baseClass, "-fixed-width-column")] = fixedWidthColumn,
        _a["".concat(baseClass, "-expansion")] = isCellExpandable,
        _a["".concat(baseClass, "-").concat(highlight)] = highlight,
        _a), className);
    var getSortIconClasses = function (direction) {
        var _a;
        return getClassNameList("".concat(baseClass, "-icon"), (_a = {},
            _a["".concat(baseClass, "-icon-hidden")] = sortDirection !== direction,
            _a));
    };
    var onClickHandler = function (e) {
        var _a;
        if (isSortable) {
            var getNewSortDirection_1 = function (dir) {
                return dir === UitkTableSortDirection.get('ascending')
                    ? UitkTableSortDirection.get('descending')
                    : UitkTableSortDirection.get('ascending');
            };
            onSort === null || onSort === void 0 ? void 0 : onSort(getNewSortDirection_1(sortDirection), id);
            (_a = tableContext === null || tableContext === void 0 ? void 0 : tableContext.onSort) === null || _a === void 0 ? void 0 : _a.call(tableContext, getNewSortDirection_1(sortDirection), id);
            setSortDirection(function (prevState) {
                var newDirection = getNewSortDirection_1(prevState);
                var announcement = newDirection === UitkTableSortDirection.get('ascending')
                    ? formatText('table.sorted.a11yLabel.ascended')
                    : formatText('table.sorted.a11yLabel.descended');
                liveAnnounce(announcement);
                return newDirection;
            });
        }
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    };
    var randomId = Math.random().toString(36).substring(7);
    var uniqueLabelId = "aria-label-".concat(randomId);
    // Creates the span for optionally adding aria descriptions to cells
    var AriaDescription = function () {
        if (description) {
            return (React.createElement("span", { className: "is-visually-hidden", "aria-hidden": true, id: uniqueLabelId }, description));
        }
        return React.createElement(React.Fragment, null);
    };
    return isHeaderCell && isSortable ? (React.createElement(Tag, { className: cellClassNames, style: { width: columnWidth }, scope: scope },
        React.createElement("button", { "aria-label": sortDirection === UitkTableSortDirection.get('ascending')
                ? formatText('table.sort.a11yLabel.descending')
                : formatText('table.sort.a11yLabel.ascending'), type: "button", className: "uitk-table-header-button", onClick: onClickHandler },
            children,
            !sortDirection && React.createElement(UitkIcon, { size: UitkIconSize.EXTRA_SMALL, name: UitkTableSortIconName.default }),
            React.createElement(UitkIcon, { size: UitkIconSize.EXTRA_SMALL, name: UitkTableSortIconName.ascending, className: getSortIconClasses(UitkTableSortDirection.get('ascending')) }),
            React.createElement(UitkIcon, { size: UitkIconSize.EXTRA_SMALL, name: UitkTableSortIconName.descending, className: getSortIconClasses(UitkTableSortDirection.get('descending')) })))) : (React.createElement(Tag, { className: cellClassNames, colSpan: colSpan, style: { width: isHeaderCell ? columnWidth : '' }, scope: isHeaderCell ? scope : undefined, "aria-describedby": description ? uniqueLabelId : undefined },
        children,
        React.createElement(AriaDescription, null)));
};
var defaultProps = {
    align: {
        horizontal: 'leading',
        vertical: 'middle'
    },
    padding: UitkTableCellPadding.standard,
    scope: UitkTableCellHeaderScope.column
};
UitkTableCell.defaultProps = defaultProps;
UitkTableCell.displayName = 'UitkTableCell';

export { UitkTableCell };
